// LxErrorState — Lamtix-custom error feedback primitive (Track B1)
//
// RTL audit:
//   - Layout is flex-row with logical gap; no directional issues.
//   - Icon (AlertTriangle) is decorative; shrink-0 prevents flex squeeze.
//   - Font family is locale-derived (Arabic/Latin) via inline style only (not a color
//     token — typography vars are exempt from the Tailwind-class-only color rule).
//   - D-AR-FIRST: Arabic text is primary; titleAr/bodyAr are the default surface.
//   - D-NUMBERS-EN-01: no numeric content; rule is trivially satisfied.
//
// Binding idiom (D-LX-BINDING-IDIOM-01 #172):
//   This is a Lamtix-custom primitive (NOT Make-adopted) → Tailwind semantic classes
//   via the lx bridge (bg-lx-danger-50, border-lx-danger-200, text-lx-danger-700,
//   bg-lx-primary-500, hover:bg-lx-primary-600).
//   NO inline var(--lx-*) color references; colors resolved exclusively through
//   Tailwind's color config (tailwind.config.ts lx.* scale → var(--lx-*) tokens).
//
// Width: intentionally width-agnostic — fills parent.
//   Detail pages (maxWidth: 1040) wrap this in their own container.
//   List pages inherit maxWidth from their parent content area.
//
// D-SHADCN-LAMTIX-WRAPPED-01: sole import path for callers is @lamtix/ui.

import { AlertTriangle, RefreshCcw } from 'lucide-react';

const DEFAULT_BODY_AR =
  'حدث خطأ مؤقت في الاتصال. حاول من جديد، وإن استمر بلّغ المسؤول.';
const DEFAULT_BODY_EN =
  'A temporary connection issue. Try again, and let your admin know if it keeps happening.';

export interface LxErrorStateProps {
  locale: 'ar' | 'en';
  /** Arabic title — required; varies per page context (e.g. list vs detail). */
  titleAr: string;
  /** English title — required; varies per page context. */
  titleEn: string;
  /** Called when the user clicks "إعادة المحاولة" / "Try again". */
  onRetry: () => void;
  /** Arabic body text. Defaults to the standard connection-error message. */
  bodyAr?: string;
  /** English body text. Defaults to the standard connection-error message. */
  bodyEn?: string;
}

export function LxErrorState({
  locale,
  titleAr,
  titleEn,
  onRetry,
  bodyAr = DEFAULT_BODY_AR,
  bodyEn = DEFAULT_BODY_EN,
}: LxErrorStateProps) {
  const isAr = locale === 'ar';
  const font = isAr ? 'var(--lx-font-arabic)' : 'var(--lx-font-latin)';

  return (
    <div
      data-testid="lx-error-state"
      role="alert"
      className="bg-lx-danger-50 border border-lx-danger-200 rounded-[14px] p-[22px] flex gap-[14px] items-start"
    >
      <AlertTriangle
        size={22}
        className="text-lx-danger-700 shrink-0"
      />
      <div className="flex-1">
        {/* Title — h3 typography (locale-aware sizes; font-family via CSS var) */}
        <div
          className="text-lx-danger-700"
          style={{
            fontFamily: font,
            fontSize: isAr ? 19 : 17,
            fontWeight: 600,
            lineHeight: isAr ? 1.55 : 1.35,
          }}
        >
          {isAr ? titleAr : titleEn}
        </div>

        {/* Body — standard connection-error message (identical across all call-sites) */}
        <p
          className="text-lx-danger-700"
          style={{
            fontFamily: font,
            fontSize: isAr ? 15 : 14,
            fontWeight: isAr ? 500 : 400,
            lineHeight: isAr ? 1.8 : 1.55,
            margin: '4px 0 12px',
          }}
        >
          {isAr ? bodyAr : bodyEn}
        </p>

        {/* Retry button — brand green (--lx-primary-500) per Board identity framing */}
        <button
          data-testid="lx-error-state-retry-button"
          onClick={onRetry}
          className="bg-lx-primary-500 hover:bg-lx-primary-600 text-white border-0 rounded-[10px] font-semibold inline-flex items-center gap-2 cursor-pointer min-h-touch px-4 transition-colors duration-[120ms]"
          style={{
            fontFamily: font,
            fontSize: isAr ? 14 : 13,
          }}
        >
          <RefreshCcw size={14} />
          {isAr ? 'إعادة المحاولة' : 'Try again'}
        </button>
      </div>
    </div>
  );
}
